import { useQuery, useMutation } from "react-query"
import qs from "query-string"
import toast from "react-hot-toast"

import { apiRequest } from "util/request"

/** ------------------- **/
/*  Attribution / Ads / Customers
/** ------------------- **/

export function useAdsOrder({
  orderId,
  date,
  groupId,
}: {
  orderId: string
  date: string
  groupId: string | undefined
}) {
  const cacheKey = ["adsOrder", orderId, date, groupId]
  const paramString = qs.stringify({
    order_id: orderId,
    created_at: date,
    group_id: groupId,
  })
  const query = () => apiRequest(`ads/order?${paramString}`)
  return useQuery(cacheKey, query, { enabled: !!orderId && !!groupId })
}

export function useAttributionOverview({
  groupId,
  startDate,
  endDate,
}: {
  groupId: string | undefined
  startDate?: string
  endDate?: string
}) {
  const cacheKey = ["adsOverview", groupId, startDate, endDate]
  const paramString = qs.stringify({
    group_id: groupId,
    start_date: startDate,
    end_date: endDate,
  })
  const query = () => apiRequest(`ads/overview?${paramString}`)
  return useQuery(cacheKey, query, { enabled: !!groupId })
}

export function useAttributionPaidAds({
  groupId,
  startDate,
  endDate,
}: {
  groupId: string | undefined
  startDate: string
  endDate: string
}) {
  const cacheKey = ["attributionPaidAds", groupId, startDate, endDate]
  const paramString = qs.stringify({
    group_id: groupId,
    start_date: startDate,
    end_date: endDate,
  })
  const query = () => apiRequest(`ads/paid_abs?${paramString}`)
  return useQuery(cacheKey, query, { enabled: !!groupId })
}

export function useAttributionEmailSms({
  groupId,
  startDate,
  endDate,
  options,
}: {
  groupId: string | undefined
  startDate: string
  endDate: string
  options?: {
    enabled?: boolean
  }
}) {
  const enabled = options?.enabled || true
  const cacheKey = ["attributionEmailSms", groupId, startDate, endDate]
  const paramString = qs.stringify({
    group_id: groupId,
    start_date: startDate,
    end_date: endDate,
  })
  const query = () => apiRequest(`ads/email_sms?${paramString}`)
  return useQuery(cacheKey, query, { enabled: !!groupId && enabled })
}

export function useAttributionOverviewTimeseries({
  groupId,
  startDate,
  endDate,
}: {
  groupId: string | undefined
  startDate: string
  endDate: string
}) {
  const cacheKey = ["adsOverviewTimeseries", groupId, startDate, endDate]
  const paramString = qs.stringify({
    group_id: groupId,
    start_date: startDate,
    end_date: endDate,
  })
  const query = () => apiRequest(`ads/overview/timeseries?${paramString}`)
  return useQuery(cacheKey, query, { enabled: !!groupId })
}

export function useAdsRecentOrders({
  groupId,
}: {
  groupId: string | undefined
}) {
  const cacheKey = ["adsRecentOrders", groupId]
  const paramString = qs.stringify({
    group_id: groupId,
  })
  const query = () => apiRequest(`ads/recent_orders?${paramString}`)
  return useQuery(cacheKey, query, {
    enabled: !!groupId,
    meta: {
      disableCache: true,
    },
  })
}

/**
 * INFLUENCERS ENDPOINTS
 */

export function useCreateInfluencerCampaign() {
  return useMutation(
    ({
      groupId,
      discountCode,
      baseUrls,
      campaignName,
      influencerId,
      campaignCost,
    }: {
      groupId: string
      discountCode: string
      baseUrls: string[]
      campaignName: string
      influencerId: number
      campaignCost: any
    }) => {
      const paramString = qs.stringify({
        group_id: groupId,
      })
      return apiRequest(
        `ads/create_influencer_campaign?${paramString}`,
        "POST",
        {
          discount_code: discountCode,
          base_urls: baseUrls,
          campaign_name: campaignName,
          influencer_id: influencerId,
          campaign_cost: campaignCost,
        }
      )
    },
    {
      onSuccess: () => {
        toast.success("Influencer campaign has been created successfully")
      },
      onError: (error: any) => {
        toast.error(error.message)
      },
    }
  )
}

export function useGetDiscountCodes({
  groupId,
  name,
}: {
  groupId: string
  name: string
}) {
  const cacheKey = ["adsGetDiscountCodes", groupId, name]
  const paramString = qs.stringify({
    group_id: groupId,
    name: name,
  })
  const query = () => apiRequest(`ads/discount_codes?${paramString}`)
  return useQuery(cacheKey, query, { enabled: !!groupId && !!name?.length })
}

export function useGetInfluencers({ groupId }: { groupId: string }) {
  const cacheKey = ["adsGetInfluencers", groupId]
  const paramString = qs.stringify({
    group_id: groupId,
  })
  const query = () => apiRequest(`ads/get_influencers?${paramString}`)
  return useQuery(cacheKey, query, {
    enabled: !!groupId,
  })
}

export function useAddInfluencer() {
  return useMutation(
    ({
      groupId,
      name,
      instagramHandle,
      tiktokHandle,
    }: {
      groupId: string
      name: string
      instagramHandle: string
      tiktokHandle: string
    }) => {
      const paramString = qs.stringify({
        group_id: groupId,
      })
      return apiRequest(`ads/create_influencer?${paramString}`, "POST", {
        name: name,
        instagram_handle: instagramHandle,
        tiktok_handle: tiktokHandle,
      })
    },
    {
      onSuccess: () => {
        toast.success("Influencer has been added successfully")
      },
      onError: (error: any) => {
        toast.error(error.message)
      },
    }
  )
}

export function useGetInfluencerCampaigns({
  groupId,
  startDate,
  endDate,
}: {
  groupId: string
  startDate: string
  endDate: string
}) {
  const cacheKey = ["adsGetInfluencerCampaigns", groupId, startDate, endDate]
  const paramString = qs.stringify({
    group_id: groupId,
    start_date: startDate,
    end_date: endDate,
  })
  const query = () => apiRequest(`ads/influencers?${paramString}`)
  return useQuery(cacheKey, query, {
    enabled: !!groupId,
  })
}

export function useRefreshLatestDiscountCodes() {
  return useMutation(
    ({ groupId }: { groupId: string }) => {
      const paramString = qs.stringify({
        group_id: groupId,
      })
      return apiRequest(`ads/discount_code_collection?${paramString}`, "POST", {
        only_discount_codes: true,
      })
    },
    {
      onError: (error: any) => {
        console.error(error.message)
      },
    }
  )
}
